import { z } from 'zod';
import { EnteredNameSchema, NoteSchema, RecordIdSchema } from './identifiers';
import {
  DecimalStringSchema,
  MoneySchema,
  addMoney,
  compareMoney,
  divideMoney,
  isNegativeMoney,
  money,
  multiplyMoney,
  subtractMoney,
  type DecimalString,
  type Money,
} from './money';
import { BusinessDateSchema, InstantSchema, type BusinessDate } from './time';

/**
 * Marketing — `MKT-*` (ADR-0025, ADR-0026), in the owner-approved **manual tracking mode** (ADR-0042).
 *
 * For this delivery the owner approved manual campaign tracking instead of automatic Meta
 * synchronization. A person creates the campaign here, maintains its running status by hand, and
 * enters or imports its dated performance figures. Those figures are a **ledger of what someone typed
 * or uploaded**, with provenance and corrections — never a provider measurement. A campaign's external
 * URL is a reference a person can open; nothing on the server fetches it, and pasting it retrieves
 * nothing.
 *
 * The honesty is structural, not a label bolted on:
 *
 * - There is **no `published` state** and no publish endpoint. `active`, `paused` and `ended` are
 *   statuses a person maintains to mirror the platform, never a provider's delivery status.
 * - Every figure carries its source (`manual` or `import`), who recorded it and when; a correction
 *   supersedes the earlier entry and both stay visible.
 * - `providerConnected` is `false` and `dataMode` is `manual` on every response.
 * - `demoMetrics` is the demonstration seed's legacy structure; no screen or report reads it any more.
 */

export const CAMPAIGN_PLATFORMS = ['facebook', 'instagram', 'google', 'tiktok', 'other'] as const;
export const CampaignPlatformSchema = z.enum(CAMPAIGN_PLATFORMS);
export type CampaignPlatform = z.infer<typeof CampaignPlatformSchema>;

export const CAMPAIGN_OBJECTIVES = [
  'leadGeneration',
  'awareness',
  'traffic',
  'engagement',
  'conversions',
] as const;
export const CampaignObjectiveSchema = z.enum(CAMPAIGN_OBJECTIVES);
export type CampaignObjective = z.infer<typeof CampaignObjectiveSchema>;

/**
 * Lifecycle. `readyToPublish` means a person has finished configuring it here. `active`, `paused` and
 * `ended` are **manually maintained** running statuses (ADR-0042): a person records that the campaign
 * runs on the platform, is paused or has finished. There is still no `published` state.
 */
export const CAMPAIGN_STATES = [
  'draft',
  'readyToPublish',
  'active',
  'paused',
  'ended',
  'archived',
] as const;
export const CampaignStateSchema = z.enum(CAMPAIGN_STATES);
export type CampaignState = z.infer<typeof CampaignStateSchema>;

/** The statuses a person maintains by hand to mirror what is happening on the platform. */
export const MANUAL_RUNNING_STATES: readonly CampaignState[] = ['active', 'paused', 'ended'];

export const CAMPAIGN_TRANSITIONS: Readonly<Record<CampaignState, readonly CampaignState[]>> = {
  draft: ['readyToPublish', 'active', 'archived'],
  readyToPublish: ['draft', 'active', 'archived'],
  active: ['paused', 'ended'],
  paused: ['active', 'ended'],
  ended: ['archived'],
  archived: ['draft'],
};

export function canTransitionCampaign(from: CampaignState, to: CampaignState): boolean {
  return CAMPAIGN_TRANSITIONS[from].includes(to);
}

/**
 * The demonstration seed's figures. Kept so existing records still parse; **nothing reads them** in
 * manual mode — performance comes from the ledger below. A new campaign stores zeros.
 */
export const DemoMetricsSchema = z.strictObject({
  impressions: z.number().int().nonnegative(),
  reach: z.number().int().nonnegative(),
  clicks: z.number().int().nonnegative(),
  leads: z.number().int().nonnegative(),
  spend: MoneySchema,
});
export type DemoMetrics = z.infer<typeof DemoMetricsSchema>;

/**
 * A campaign's external URL — a **reference only** (ADR-0042): `http(s)`, a host with a dot, no
 * embedded credentials, no whitespace, bounded. The server never fetches it; the screen opens it in a
 * new tab without the opener. Pasting the URL of a private Ads Manager page grants no access to it.
 */
export function isSafeExternalUrl(value: string): boolean {
  if (/\s/.test(value)) return false;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
  if (url.username || url.password) return false;
  return url.hostname.includes('.');
}

export const ExternalUrlSchema = z
  .string()
  .trim()
  .max(2000)
  .refine(isSafeExternalUrl, { message: 'EXTERNAL_URL_EXPECTED' });

/** How a campaign's performance is recorded. Fixed by its first entry; the two never mix. */
export const PERFORMANCE_KINDS = ['daily', 'cumulative'] as const;
export const PerformanceKindSchema = z.enum(PERFORMANCE_KINDS);
export type PerformanceKind = z.infer<typeof PerformanceKindSchema>;

export const CampaignSchema = z.strictObject({
  campaignId: RecordIdSchema,
  name: EnteredNameSchema,
  platform: CampaignPlatformSchema,
  objective: CampaignObjectiveSchema,
  state: CampaignStateSchema,
  budget: MoneySchema,
  startsOn: BusinessDateSchema,
  endsOn: BusinessDateSchema.optional(),
  projectId: RecordIdSchema.optional(),
  /** The responsible person. `self` and `assigned` scopes resolve against this. */
  ownerAccountId: z.string().min(1).max(200),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  /** Free text describing who the campaign targets. No audience is created anywhere. */
  audienceSummary: NoteSchema.optional(),
  /** Metadata about the creative: a headline and a description. No file is uploaded (`CORE-DOC`). */
  creativeHeadline: z.string().trim().max(200).optional(),
  creativeBody: NoteSchema.optional(),
  /** Legacy demonstration figures; not read by any screen or report (see the file comment). */
  demoMetrics: DemoMetricsSchema,
  /** A reference a person can open. Never fetched by the server (ADR-0042). */
  externalUrl: ExternalUrlSchema.optional(),
  /** Daily totals or cumulative snapshots — set by the first performance entry, then fixed. */
  trackingMode: PerformanceKindSchema.optional(),
  /**
   * The provider's own campaign identifier. **Always absent**, because nothing is published. It is in
   * the schema so that connecting a provider adds a value rather than reinterpreting a field.
   */
  externalReference: z.string().max(200).optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Campaign = z.infer<typeof CampaignSchema>;

export const CreateCampaignSchema = z.strictObject({
  name: EnteredNameSchema,
  platform: CampaignPlatformSchema,
  objective: CampaignObjectiveSchema,
  budget: MoneySchema,
  startsOn: BusinessDateSchema,
  endsOn: BusinessDateSchema.optional(),
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  audienceSummary: NoteSchema.optional(),
  creativeHeadline: z.string().trim().max(200).optional(),
  creativeBody: NoteSchema.optional(),
  externalUrl: ExternalUrlSchema.optional(),
  /** The responsible person; the creator when absent. Must be an active account. */
  ownerAccountId: z.string().min(1).max(200).optional(),
});
export type CreateCampaign = z.infer<typeof CreateCampaignSchema>;

export const UpdateCampaignSchema = z.strictObject({
  name: EnteredNameSchema.optional(),
  objective: CampaignObjectiveSchema.optional(),
  budget: MoneySchema.optional(),
  endsOn: BusinessDateSchema.optional(),
  projectId: RecordIdSchema.optional(),
  audienceSummary: NoteSchema.optional(),
  creativeHeadline: z.string().trim().max(200).optional(),
  creativeBody: NoteSchema.optional(),
  state: CampaignStateSchema.optional(),
  /** `null` removes the reference. */
  externalUrl: ExternalUrlSchema.nullable().optional(),
  ownerAccountId: z.string().min(1).max(200).optional(),
  /** Why a status or the responsible person changed; kept in the audit trail. */
  reason: z.string().trim().max(500).optional(),
  expectedVersion: z.number().int().positive().optional(),
});
export type UpdateCampaign = z.infer<typeof UpdateCampaignSchema>;

export const CampaignQuerySchema = z.strictObject({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().min(1).max(200).optional(),
  platform: CampaignPlatformSchema.optional(),
  state: CampaignStateSchema.optional(),
  projectId: RecordIdSchema.optional(),
  ownerAccountId: z.string().min(1).max(200).optional(),
});
export type CampaignQuery = z.infer<typeof CampaignQuerySchema>;

export const CampaignPageSchema = z.strictObject({
  items: z.array(CampaignSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
  /** Always `false`. Stated on the response so no screen can imply otherwise (ADR-0026). */
  providerConnected: z.literal(false),
  /** Always `manual`: figures are entered or imported by people (ADR-0042). */
  dataMode: z.literal('manual'),
});
export type CampaignPage = z.infer<typeof CampaignPageSchema>;

/**
 * The marketing overview.
 *
 * Spend and leads come from the manual performance ledger (ADR-0042); `totalLeads` counts leads the
 * **platform reported**, not CRM records. `costPerLead` is spend ÷ reported leads, and is **absent**
 * when nothing was reported — a division by zero rendered as "0" or "∞" is the kind of number that
 * ends up in a board pack. Totals cover one currency; other campaigns are counted, never mixed in.
 */
export const MarketingOverviewSchema = z.strictObject({
  campaigns: z.number().int().nonnegative(),
  totalBudget: MoneySchema,
  totalSpend: MoneySchema,
  totalLeads: z.number().int().nonnegative(),
  costPerLead: MoneySchema.optional(),
  /** Campaigns budgeted in another currency, left out of these totals rather than mixed in. */
  campaignsInOtherCurrencies: z.number().int().nonnegative(),
  byPlatform: z.array(
    z.strictObject({
      platform: CampaignPlatformSchema,
      campaigns: z.number().int().nonnegative(),
      spend: MoneySchema,
      leads: z.number().int().nonnegative(),
    }),
  ),
  providerConnected: z.literal(false),
  dataMode: z.literal('manual'),
});
export type MarketingOverview = z.infer<typeof MarketingOverviewSchema>;

/* ------------------------------------------------------------ performance ledger */

/** Where an entry came from. Neither is a provider measurement. */
export const PERFORMANCE_SOURCES = ['manual', 'import'] as const;
export const PerformanceSourceSchema = z.enum(PERFORMANCE_SOURCES);
export type PerformanceSource = z.infer<typeof PerformanceSourceSchema>;

/** `superseded` by a correction, or `withdrawn` because it was wrong. Neither is ever deleted. */
export const PERFORMANCE_ENTRY_STATES = ['active', 'superseded', 'withdrawn'] as const;
export const PerformanceEntryStateSchema = z.enum(PERFORMANCE_ENTRY_STATES);
export type PerformanceEntryState = z.infer<typeof PerformanceEntryStateSchema>;

const CountSchema = z.number().int().nonnegative().max(1_000_000_000_000);
const ReasonSchema = z.string().trim().min(3).max(500);

/** The figures a platform's report gives. Only spend is required; the rest where the report has it. */
const FiguresShape = {
  spend: MoneySchema,
  impressions: CountSchema.optional(),
  reach: CountSchema.optional(),
  clicks: CountSchema.optional(),
  reportedLeads: CountSchema.optional(),
};

function figuresConsistent(value: {
  spend: Money;
  impressions?: number | undefined;
  reach?: number | undefined;
}): boolean {
  if (isNegativeMoney(value.spend)) return false;
  if (value.reach !== undefined && value.impressions !== undefined) {
    return value.reach <= value.impressions;
  }
  return true;
}
const FIGURES_REFINEMENT = { message: 'PERFORMANCE_FIGURES_INCONSISTENT' };

export const PerformanceEntrySchema = z.strictObject({
  entryId: RecordIdSchema,
  campaignId: RecordIdSchema,
  /** The day the figures are for (daily), or the day the snapshot was taken (cumulative). */
  periodDate: BusinessDateSchema,
  kind: PerformanceKindSchema,
  ...FiguresShape,
  source: PerformanceSourceSchema,
  importBatchId: RecordIdSchema.optional(),
  /** The report or export it was copied from, as the person named it. */
  sourceReference: z.string().trim().max(200).optional(),
  note: NoteSchema.optional(),
  state: PerformanceEntryStateSchema,
  supersedesEntryId: RecordIdSchema.optional(),
  supersededByEntryId: RecordIdSchema.optional(),
  /** Why it was corrected or withdrawn. */
  reason: z.string().max(500).optional(),
  recordedByAccountId: z.string().min(1).max(200),
  recordedAt: InstantSchema,
});
export type PerformanceEntry = z.infer<typeof PerformanceEntrySchema>;

export const RecordPerformanceSchema = z
  .strictObject({
    periodDate: BusinessDateSchema,
    kind: PerformanceKindSchema,
    ...FiguresShape,
    sourceReference: z.string().trim().max(200).optional(),
    note: NoteSchema.optional(),
  })
  .refine(figuresConsistent, FIGURES_REFINEMENT);
export type RecordPerformance = z.infer<typeof RecordPerformanceSchema>;

export const CorrectPerformanceSchema = z
  .strictObject({
    ...FiguresShape,
    sourceReference: z.string().trim().max(200).optional(),
    note: NoteSchema.optional(),
    reason: ReasonSchema,
  })
  .refine(figuresConsistent, FIGURES_REFINEMENT);
export type CorrectPerformance = z.infer<typeof CorrectPerformanceSchema>;

export const WithdrawPerformanceSchema = z.strictObject({ reason: ReasonSchema });
export type WithdrawPerformance = z.infer<typeof WithdrawPerformanceSchema>;

export const PerformanceQuerySchema = z.strictObject({
  includeHistory: z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => value === 'true'),
});

export const PerformanceListSchema = z.strictObject({
  items: z.array(PerformanceEntrySchema),
  trackingMode: PerformanceKindSchema.optional(),
  dataMode: z.literal('manual'),
});
export type PerformanceList = z.infer<typeof PerformanceListSchema>;

/** The campaign-performance file's columns (`CORE-IMPORT`). Header names match case-insensitively. */
export const CAMPAIGN_PERFORMANCE_IMPORT_COLUMNS = [
  /** The campaign's exact name, or its record id. */
  { name: 'campaign', required: true },
  { name: 'date', required: true },
  /** `daily` (that day's figures) or `cumulative` (totals to date). */
  { name: 'kind', required: true },
  { name: 'spend', required: true },
  { name: 'currency', required: true },
  { name: 'impressions', required: false },
  { name: 'reach', required: false },
  { name: 'clicks', required: false },
  { name: 'reported_leads', required: false },
  { name: 'source_reference', required: false },
] as const;

/* --------------------------------------------------------------- calculations */

type FigureKey = 'impressions' | 'reach' | 'clicks' | 'reportedLeads';
const ADDITIVE_COUNTS: readonly FigureKey[] = ['impressions', 'clicks', 'reportedLeads'];

export interface PerformanceFigures {
  periodDate: BusinessDate;
  spend: Money;
  impressions?: number | undefined;
  reach?: number | undefined;
  clicks?: number | undefined;
  reportedLeads?: number | undefined;
}

export interface PerformanceTotals {
  entries: number;
  /** Absent when it cannot be stated honestly (see `performanceTotals`). */
  spend?: Money;
  impressions?: number;
  reach?: number;
  clicks?: number;
  reportedLeads?: number;
  /** The latest date the figures cover. */
  asOf?: BusinessDate;
  /** Cumulative mode with a `from`: the snapshot subtracted to obtain the period. */
  baselineDate?: BusinessDate;
}

/**
 * Totals over a campaign's **active** entries, in its one tracking mode.
 *
 * - **Daily:** sums the days inside the range. A count is stated only when every included day reports
 *   it (a partial sum would understate). **Reach is never summed across days** — it counts unique
 *   people, so the sum of daily reach double-counts; it is absent in daily mode.
 * - **Cumulative:** never summed. The figure is the latest snapshot on or before `to`; with a `from`,
 *   the latest snapshot before `from` is subtracted to give the period. A subtraction that would go
 *   negative (a platform's later correction) is absent, not clamped. Reach is stated only without a
 *   baseline, because unique-people totals cannot be subtracted either.
 */
export function performanceTotals(
  entries: readonly PerformanceFigures[],
  kind: PerformanceKind | undefined,
  currency: string,
  range: { from?: string | undefined; to?: string | undefined } = {},
): PerformanceTotals {
  const inRange = (date: string) =>
    (range.from === undefined || date >= range.from) &&
    (range.to === undefined || date <= range.to);
  if (!kind || entries.length === 0) return { entries: 0, spend: money('0', currency) };

  if (kind === 'daily') {
    const included = entries.filter((entry) => inRange(entry.periodDate));
    if (included.length === 0) return { entries: 0, spend: money('0', currency) };
    const totals: PerformanceTotals = {
      entries: included.length,
      spend: included.reduce((sum, entry) => addMoney(sum, entry.spend), money('0', currency)),
      asOf: included
        .map((entry) => entry.periodDate)
        .sort()
        .at(-1)!,
    };
    for (const key of ADDITIVE_COUNTS) {
      if (included.every((entry) => entry[key] !== undefined)) {
        totals[key] = included.reduce((sum, entry) => sum + (entry[key] ?? 0), 0);
      }
    }
    return totals;
  }

  const sorted = [...entries].sort((a, b) => (a.periodDate < b.periodDate ? -1 : 1));
  const end = [...sorted]
    .reverse()
    .find((entry) => range.to === undefined || entry.periodDate <= range.to);
  if (!end || (range.from !== undefined && end.periodDate < range.from)) {
    return { entries: 0, spend: money('0', currency) };
  }
  const start =
    range.from === undefined
      ? undefined
      : [...sorted].reverse().find((entry) => entry.periodDate < range.from!);
  const totals: PerformanceTotals = {
    entries: sorted.filter((entry) => inRange(entry.periodDate)).length,
    asOf: end.periodDate,
    ...(start ? { baselineDate: start.periodDate } : {}),
  };
  const spend = start ? subtractMoney(end.spend, start.spend) : end.spend;
  if (!isNegativeMoney(spend)) totals.spend = spend;
  for (const key of ADDITIVE_COUNTS) {
    const last = end[key];
    if (last === undefined) continue;
    if (!start) totals[key] = last;
    else {
      const first = start[key];
      if (first !== undefined && last - first >= 0) totals[key] = last - first;
    }
  }
  if (!start && end.reach !== undefined) totals.reach = end.reach;
  return totals;
}

/** `numerator ÷ denominator × 100` to two places, half-up — absent for a zero denominator. */
export function ratioPercent(numerator: number, denominator: number): DecimalString | undefined {
  if (!Number.isInteger(numerator) || !Number.isInteger(denominator) || denominator <= 0) {
    return undefined;
  }
  return divideMoney(money(String(numerator * 100), 'XXX'), String(denominator), 2, 'halfUp')
    .amount;
}

/** Money ÷ count to two places, half-up — absent for a zero count or an absent amount. */
export function costPer(amount: Money | undefined, count: number | undefined): Money | undefined {
  if (!amount || count === undefined || count <= 0) return undefined;
  return divideMoney(amount, String(count), 2, 'halfUp');
}

/* ----------------------------------------------------------------- attribution */

/**
 * The attribution rule (ADR-0042), stated once so every screen and report says the same thing:
 *
 * A CRM lead is **linked** to a campaign when its `campaignId` names it (set when the lead is captured or
 * imported, and not changed afterwards). A reservation is **attributed** to the campaign of the lead it was raised
 * from; a contract through its reservation. A reservation has at most one lead and a lead at most one
 * campaign, so no reservation or contract is counted twice in any aggregate. Contracted value is the
 * contract price — **not cash collected and not profit**.
 */
export const ATTRIBUTION_RULE = 'leadCampaign' as const;

/** Lead stages counted as qualified — at or beyond qualification, excluding a lost lead. */
export const QUALIFIED_LEAD_STAGES = [
  'qualified',
  'visitScheduled',
  'negotiation',
  'reservation',
  'won',
] as const;

/**
 * Reservation states counted as a reservation made: confirmed, or converted into a contract.
 * Unconfirmed, rejected, cancelled and expired reservations are not counted.
 */
export const ATTRIBUTED_RESERVATION_STATES = ['confirmed', 'converted'] as const;

/** Contract states counted as a contracted sale. */
export const ATTRIBUTED_CONTRACT_STATES = ['active', 'completed'] as const;

export const CampaignAttributionSchema = z.strictObject({
  /** Absent: the reader may not read leads (`crm.lead.view`). */
  leads: z
    .strictObject({
      linked: z.number().int().nonnegative(),
      qualified: z.number().int().nonnegative(),
    })
    .optional(),
  /** Absent: the reader may not read reservations and contracts. */
  sales: z
    .strictObject({
      reservations: z.number().int().nonnegative(),
      contracts: z.number().int().nonnegative(),
      /** One entry per contract currency. Contract price — not cash collected, not profit. */
      contractedValue: z.array(MoneySchema),
    })
    .optional(),
});
export type CampaignAttribution = z.infer<typeof CampaignAttributionSchema>;

export const CampaignMetricsSchema = z.strictObject({
  trackingMode: PerformanceKindSchema.optional(),
  entries: z.number().int().nonnegative(),
  asOf: BusinessDateSchema.optional(),
  baselineDate: BusinessDateSchema.optional(),
  spend: MoneySchema.optional(),
  impressions: CountSchema.optional(),
  reach: CountSchema.optional(),
  clicks: CountSchema.optional(),
  reportedLeads: CountSchema.optional(),
  /** Clicks ÷ impressions. */
  ctrPercent: DecimalStringSchema.optional(),
  /** Spend ÷ leads the platform reported. */
  costPerReportedLead: MoneySchema.optional(),
  /** Spend ÷ CRM leads linked to the campaign — a different figure, labelled differently. */
  costPerLinkedLead: MoneySchema.optional(),
  budgetUsedPercent: DecimalStringSchema.optional(),
  remainingBudget: MoneySchema.optional(),
  qualifiedPercent: DecimalStringSchema.optional(),
  leadToReservationPercent: DecimalStringSchema.optional(),
  reservationToContractPercent: DecimalStringSchema.optional(),
  attribution: CampaignAttributionSchema,
});
export type CampaignMetrics = z.infer<typeof CampaignMetricsSchema>;

/** Every derived figure from validated inputs; an undefined denominator leaves the figure absent. */
export function campaignMetrics(
  totals: PerformanceTotals,
  trackingMode: PerformanceKind | undefined,
  budget: Money,
  attribution: CampaignAttribution,
): CampaignMetrics {
  const spend = totals.spend;
  const linked = attribution.leads?.linked;
  const reservations = attribution.sales?.reservations;
  const ctr =
    totals.clicks !== undefined && totals.impressions !== undefined
      ? ratioPercent(totals.clicks, totals.impressions)
      : undefined;
  const costPerReported = costPer(spend, totals.reportedLeads);
  const costPerLinked = costPer(spend, linked);
  const budgetPositive = compareMoney(budget, money('0', budget.currency)) > 0;
  const budgetUsed =
    spend && budgetPositive && spend.currency === budget.currency
      ? divideMoney(multiplyMoney(spend, '100'), budget.amount, 2, 'halfUp').amount
      : undefined;
  const qualified =
    attribution.leads && linked !== undefined
      ? ratioPercent(attribution.leads.qualified, linked)
      : undefined;
  const toReservation =
    reservations !== undefined && linked !== undefined
      ? ratioPercent(reservations, linked)
      : undefined;
  const toContract =
    attribution.sales && reservations !== undefined
      ? ratioPercent(attribution.sales.contracts, reservations)
      : undefined;
  return {
    ...(trackingMode ? { trackingMode } : {}),
    entries: totals.entries,
    ...(totals.asOf ? { asOf: totals.asOf } : {}),
    ...(totals.baselineDate ? { baselineDate: totals.baselineDate } : {}),
    ...(spend ? { spend } : {}),
    ...(totals.impressions !== undefined ? { impressions: totals.impressions } : {}),
    ...(totals.reach !== undefined ? { reach: totals.reach } : {}),
    ...(totals.clicks !== undefined ? { clicks: totals.clicks } : {}),
    ...(totals.reportedLeads !== undefined ? { reportedLeads: totals.reportedLeads } : {}),
    ...(ctr ? { ctrPercent: ctr } : {}),
    ...(costPerReported ? { costPerReportedLead: costPerReported } : {}),
    ...(costPerLinked ? { costPerLinkedLead: costPerLinked } : {}),
    ...(budgetUsed ? { budgetUsedPercent: budgetUsed } : {}),
    ...(spend && spend.currency === budget.currency
      ? { remainingBudget: subtractMoney(budget, spend) }
      : {}),
    ...(qualified ? { qualifiedPercent: qualified } : {}),
    ...(toReservation ? { leadToReservationPercent: toReservation } : {}),
    ...(toContract ? { reservationToContractPercent: toContract } : {}),
    attribution,
  };
}

/* ---------------------------------------------------------------------- report */

const RangeShape = {
  from: BusinessDateSchema.optional(),
  to: BusinessDateSchema.optional(),
};

export const CampaignReportQuerySchema = z
  .strictObject({
    ...RangeShape,
    platform: CampaignPlatformSchema.optional(),
    projectId: RecordIdSchema.optional(),
    state: CampaignStateSchema.optional(),
  })
  .refine((value) => !value.from || !value.to || value.from <= value.to, {
    message: 'RANGE_INVALID',
  });
export type CampaignReportQuery = z.infer<typeof CampaignReportQuerySchema>;

export const CampaignReportRowSchema = z.strictObject({
  campaignId: RecordIdSchema,
  name: EnteredNameSchema,
  platform: CampaignPlatformSchema,
  state: CampaignStateSchema,
  projectId: RecordIdSchema.optional(),
  ownerAccountId: z.string().min(1).max(200),
  budget: MoneySchema,
  startsOn: BusinessDateSchema,
  endsOn: BusinessDateSchema.optional(),
  externalUrl: ExternalUrlSchema.optional(),
  metrics: CampaignMetricsSchema,
});
export type CampaignReportRow = z.infer<typeof CampaignReportRowSchema>;

export const CampaignReportTotalSchema = z.strictObject({
  /** The campaigns' budget currency. */
  currency: z.string(),
  campaigns: z.number().int().nonnegative(),
  budget: MoneySchema,
  /** Sum over campaigns whose spend could be stated. */
  spend: MoneySchema,
  campaignsWithoutSpend: z.number().int().nonnegative(),
  reportedLeads: z.number().int().nonnegative(),
  campaignsWithoutReportedLeads: z.number().int().nonnegative(),
  linkedLeads: z.number().int().nonnegative().optional(),
  qualifiedLeads: z.number().int().nonnegative().optional(),
  reservations: z.number().int().nonnegative().optional(),
  contracts: z.number().int().nonnegative().optional(),
  /** Only when every campaign stated both spend and reported leads. */
  costPerReportedLead: MoneySchema.optional(),
  costPerLinkedLead: MoneySchema.optional(),
});
export type CampaignReportTotal = z.infer<typeof CampaignReportTotalSchema>;

export const CampaignReportSchema = z.strictObject({
  from: BusinessDateSchema.optional(),
  to: BusinessDateSchema.optional(),
  rows: z.array(CampaignReportRowSchema),
  totals: z.array(CampaignReportTotalSchema),
  /** Contract price by contract currency across the rows. Not cash collected, not profit. */
  contractedValue: z.array(MoneySchema),
  /** With a range, attribution covers leads captured in the range and their outcomes to date. */
  attributionRule: z.literal(ATTRIBUTION_RULE),
  truncated: z.boolean(),
  generatedAt: InstantSchema,
  providerConnected: z.literal(false),
  dataMode: z.literal('manual'),
});
export type CampaignReport = z.infer<typeof CampaignReportSchema>;

/** Totals per budget currency, never mixing currencies; partial figures are counted, not hidden. */
export function campaignReportTotals(rows: readonly CampaignReportRow[]): CampaignReportTotal[] {
  const byCurrency = new Map<string, CampaignReportRow[]>();
  for (const row of rows) {
    const list = byCurrency.get(row.budget.currency) ?? [];
    list.push(row);
    byCurrency.set(row.budget.currency, list);
  }
  return [...byCurrency.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([currency, list]) => {
      const zero = money('0', currency);
      let spend = zero;
      let withoutSpend = 0;
      let reported = 0;
      let withoutReported = 0;
      for (const row of list) {
        if (row.metrics.spend && row.metrics.spend.currency === currency) {
          spend = addMoney(spend, row.metrics.spend);
        } else withoutSpend += 1;
        if (row.metrics.reportedLeads !== undefined) reported += row.metrics.reportedLeads;
        else withoutReported += 1;
      }
      const sum = (pick: (row: CampaignReportRow) => number | undefined) =>
        list.every((row) => pick(row) !== undefined)
          ? list.reduce((total, row) => total + (pick(row) ?? 0), 0)
          : undefined;
      const linked = sum((row) => row.metrics.attribution.leads?.linked);
      const qualified = sum((row) => row.metrics.attribution.leads?.qualified);
      const reservations = sum((row) => row.metrics.attribution.sales?.reservations);
      const contracts = sum((row) => row.metrics.attribution.sales?.contracts);
      const complete = withoutSpend === 0;
      const perReported = complete && withoutReported === 0 ? costPer(spend, reported) : undefined;
      const perLinked = complete ? costPer(spend, linked) : undefined;
      return {
        currency,
        campaigns: list.length,
        budget: list.reduce((total, row) => addMoney(total, row.budget), zero),
        spend,
        campaignsWithoutSpend: withoutSpend,
        reportedLeads: reported,
        campaignsWithoutReportedLeads: withoutReported,
        ...(linked !== undefined ? { linkedLeads: linked } : {}),
        ...(qualified !== undefined ? { qualifiedLeads: qualified } : {}),
        ...(reservations !== undefined ? { reservations } : {}),
        ...(contracts !== undefined ? { contracts } : {}),
        ...(perReported ? { costPerReportedLead: perReported } : {}),
        ...(perLinked ? { costPerLinkedLead: perLinked } : {}),
      };
    });
}

export const MARKETING_AUDIT_ACTIONS = {
  campaignCreated: 'marketing.campaign.created',
  campaignUpdated: 'marketing.campaign.updated',
  campaignRefused: 'marketing.campaign.refused',
  performanceRecorded: 'marketing.performance.recorded',
  performanceCorrected: 'marketing.performance.corrected',
  performanceWithdrawn: 'marketing.performance.withdrawn',
  performanceImported: 'marketing.performance.imported',
} as const;
